<template>
  <div>
    <div class="gl-display-flex gl-align-items-center mb-3 gl-justify-content-end">
      <gl-button
        category="primary"
        variant="confirm"
        size="medium"
        :block="false"
        :disabled="false"
        @click="
          $router.push({
            name: 'AddPayoutAccount',
            params: { id: $route.params.companyId, walletId: $route.params.walletId, userId},
          })
        "
      >
        Add payout account
      </gl-button>
    </div>

    <gl-modal
      v-model="isOtpModalVisible"
      modal-id="payout-account-otp-modal"
      :title="otpModalTitle"
      :action-primary="{ text: confirmButtonText }"
      :action-cancel="{ text: 'Cancel' }"
      @primary="submitOtpAndExecuteAction"
      @cancel="closeOtpModal"
    >
      <gl-form-group label="OTP Code" label-for="otp-code">
        <gl-form-input
          id="otp-code"
          v-model="otpCode"
          type="text"
          maxlength="6"
          placeholder="Enter OTP on you authenticator app."
        />
      </gl-form-group>
    </gl-modal>

    <gl-table
      :items="payoutAccounts"
      :fields="payoutAccountFields"
      fixed
      :stacked="false"
      :foot-clone="false"
      hover
      :show-empty="true"
      :caption="payoutAccounts.length + ' payout account(s)'"
    >
      <template #cell(isActive)="data">
        <gl-badge size="md" :variant="data.value ? 'success' : 'neutral'">
          {{ data.value ? 'Yes' : 'No' }}
        </gl-badge>
      </template>
      <template #cell(status)="data">
        <gl-badge
          size="md"
          :variant="
            data.value === 'Approved'
              ? 'success'
              : data.value === 'Rejected'
              ? 'danger'
              : 'warning'
          "
        >
          {{ data.value }}
        </gl-badge>
      </template>
      <template #cell(actions)="data">
        <gl-dropdown icon="ellipsis_v" right no-caret category="tertiary" size="small">
          <template #button-content>
            <gl-icon name="ellipsis_v" />
          </template>
          <gl-dropdown-item
            v-if="data.item.status === 'Pending'"
            @click="openOtpModal('approve', data.item)"
          >
            Approve
          </gl-dropdown-item>
          <gl-dropdown-item
            v-if="data.item.status === 'Pending'"
            @click="openOtpModal('reject', data.item)"
          >
            Reject
          </gl-dropdown-item>
          <gl-dropdown-item
            v-if="!data.item.isActive"
            @click="openOtpModal('activate', data.item)"
          >
            Activate
          </gl-dropdown-item>
        </gl-dropdown>
      </template>
    </gl-table>
  </div>
</template>

<script>
import {
  GlBadge,
  GlButton,
  GlDropdown,
  GlDropdownItem,
  GlFormGroup,
  GlFormInput,
  GlIcon,
  GlModal,
  GlTable,
} from '@gitlab/ui';

export default {
  name: 'PayoutAccountsTab',
  components: {
    GlBadge,
    GlButton,
    GlDropdown,
    GlDropdownItem,
    GlFormGroup,
    GlFormInput,
    GlIcon,
    GlModal,
    GlTable,
  },
  props: {
    payoutAccounts: {
      type: Array,
      default: () => [],
    },
    userId: {
      type: [String, Number],
      default: null,
    },
    walletId: {
      type: [String, Number],
      default: null,
    },
  },
  data: () => ({
    payoutAccountFields: [
      { key: 'bankName', label: 'Bank Name' },
      { key: 'accountName', label: 'Account Name' },
      { key: 'accountNumber', label: 'Account Number' },
      { key: 'status', label: 'Status' },
      { key: 'isActive', label: 'Active' },
      { key: 'actions', label: '' },
    ],
    isOtpModalVisible: false,
    pendingAction: null,
    pendingAccount: null,
    otpCode: '',
  }),
  computed: {
    otpModalTitle() {
      if (!this.pendingAction) return 'Enter OTP';
      const titles = {
        approve: 'Approve payout account',
        reject: 'Reject payout account',
        activate: 'Activate payout account',
      };
      return titles[this.pendingAction] || 'Enter OTP';
    },
    confirmButtonText() {
      if (!this.pendingAction) return 'Confirm';
      const labels = {
        approve: 'Approve',
        reject: 'Reject',
        activate: 'Activate',
      };
      return labels[this.pendingAction] || 'Confirm';
    },
  },
  methods: {
    toggleLoading(isLoading) {
      this.$store.dispatch('loading/toggleLoading', isLoading);
    },

    openOtpModal(action, account) {
      this.pendingAction = action;
      this.pendingAccount = account;
      this.otpCode = '';
      this.isOtpModalVisible = true;
    },

    closeOtpModal() {
      this.isOtpModalVisible = false;
      this.pendingAction = null;
      this.pendingAccount = null;
      this.otpCode = '';
    },

    async submitOtpAndExecuteAction() {
      if (!this.otpCode || !this.pendingAccount || !this.pendingAction) {
        return;
      }
      this.toggleLoading(true);
      try {
        if (this.pendingAction === 'approve') {
          await this.$store.dispatch('wallet/approvePayoutAccount', {
            accountId: this.pendingAccount.id,
            otpCode: this.otpCode,
          });
        } else if (this.pendingAction === 'reject') {
          await this.$store.dispatch('wallet/rejectPayoutAccount', {
            accountId: this.pendingAccount.id,
            otpCode: this.otpCode,
          });
        } else if (this.pendingAction === 'activate') {
          await this.$store.dispatch('wallet/activatePayoutAccount', {
            accountId: this.pendingAccount.id,
            walletId: this.walletId || this.$route.params.walletId,
            otpCode: this.otpCode,
          });
        }
        this.closeOtpModal();
        this.$emit('refresh');
      } finally {
        this.toggleLoading(false);
      }
    },
  },
};
</script>
